import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "gv_token";

const AuthContext = createContext(null);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map(formatApiErrorDetail)
      .filter(Boolean)
      .join(" ");
  if (typeof detail === "object") {
    if (typeof detail.message === "string") return detail.message;
    if (typeof detail.msg === "string") return detail.msg;
    if (detail.detail != null) return formatApiErrorDetail(detail.detail);
    return "Something went wrong. Please try again.";
  }
  return String(detail);
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking / not loaded
  const [loading, setLoading] = useState(true);
  const sessionEpoch = useRef(0);

  const applyToken = (token) => {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  };

  const authHeader = () => {
    const t = localStorage.getItem(TOKEN_KEY);
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const loadMe = useCallback(async () => {
    const generation = sessionEpoch.current;
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${t}` } });
      if (generation === sessionEpoch.current) setUser(data.user);
    } catch (e) {
      if (generation === sessionEpoch.current) { applyToken(null); setUser(false); }
    } finally {
      if (generation === sessionEpoch.current) setLoading(false);
    }
  }, []);

  useEffect(() => { loadMe(); }, [loadMe]);

  const login = async (identifier, password) => {
    try {
      const { data } = await axios.post(`${API}/auth/login`, { identifier, password });
      sessionEpoch.current += 1; setLoading(false);
      applyToken(data.token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (email, password, username) => {
    try {
      const { data } = await axios.post(`${API}/auth/register`, { email, password, username });
      sessionEpoch.current += 1; setLoading(false);
      applyToken(data.token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = () => {
    sessionEpoch.current += 1; setLoading(false);
    applyToken(null);
    setUser(false);
  };
  const replaceSession = (data) => { sessionEpoch.current += 1; applyToken(data.token); setUser(data.user); setLoading(false); };
  const updateUser = (patch) => { sessionEpoch.current += 1; setUser((current) => current ? { ...current, ...patch } : current); };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh: loadMe, authHeader, replaceSession, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
